import { AlertTriangle, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAlertas } from "../hooks";
import { AlertaBadge } from "./AlertaBadge";
import type { AlertLevel } from "@cold-crew/shared";

function fmtCantidad(val: number, unidad: string) {
  if (unidad === "g" && val >= 1000) return `${(val / 1000).toFixed(2)} kg`;
  if (unidad === "ml" && val >= 1000) return `${(val / 1000).toFixed(2)} L`;
  return `${val} ${unidad}`;
}

export function AlertasPanel({ className }: { className?: string }) {
  const { data, isLoading } = useAlertas();
  const alertas = data?.data ?? [];
  const rojas   = alertas.filter((a) => a.nivel === "rojo").length;
  const amarillas = alertas.filter((a) => a.nivel === "amarillo").length;

  return (
    <div className={cn("rounded-lg border border-border bg-card", className)}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <AlertTriangle className="size-4 text-yellow-500" />
          Alertas de inventario
        </h3>
        {alertas.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {rojas > 0 && <span className="text-red-600 font-medium">{rojas} crítico{rojas !== 1 ? "s" : ""}</span>}
            {rojas > 0 && amarillas > 0 && " · "}
            {amarillas > 0 && <span className="text-yellow-600">{amarillas} bajo{amarillas !== 1 ? "s" : ""}</span>}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground text-sm gap-2">
          <span className="size-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Verificando stock…
        </div>
      ) : alertas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
          <CheckCircle className="size-6 text-emerald-500" />
          <p className="text-sm">Todo el inventario está en orden</p>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {alertas.map((alerta) => (
            <li key={alerta.producto_id} className={cn(
              "flex items-center justify-between px-4 py-3 text-sm",
              alerta.nivel === "rojo" ? "bg-red-50/50 dark:bg-red-950/10" : "bg-yellow-50/50 dark:bg-yellow-950/10"
            )}>
              <div>
                <p className="font-medium text-foreground">{alerta.producto_nombre}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Disponible: <span className="font-semibold">{fmtCantidad(alerta.cantidad_actual, alerta.unidad)}</span>
                  {" / Mínimo: "}{fmtCantidad(alerta.cantidad_minima, alerta.unidad)}
                </p>
              </div>
              <AlertaBadge nivel={alerta.nivel as AlertLevel} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
